/**
 * What a player does to blocks: break one, place one, and right-click with or
 * on one (doors, buckets, flint and steel, bone meal, hoes, seeds...).
 *
 * DOM-free rules as functions of (world, player state, arguments). The offline
 * game (main.ts), the game server (server/actions/blocks.ts) and the client's
 * prediction in server mode (client/blocks.ts) all run these same functions
 * through a `BlockEnv` (the world and its side effects) and a `BlockActor`
 * (the player). Node test: src/smoke/blockActions.ts.
 */

import {
  AIR, BARREL_ID, BED_FOOT_ID, BED_HEAD_ID, BLOCKS, BREWING_STAND_ID, CRAFTING_TABLE_ID, DOOR_BOTTOM_IDS, DOOR_STATE,
  DOOR_TOP_TO_BOTTOM, ENCHANTING_TABLE_ID, END_PORTAL_FRAME_ID, FIRE_ID, FULL_CELL_BOXES, GRINDSTONE_ID, IS_LAVA,
  IS_SOLID, IS_TARGETABLE, IS_WATERY, LAVA_ID, NETHER_WART_IDS, SHAPE_FAMILIES, SHAPE_STATE, SMITHING_TABLE_ID,
  SOUL_FIRE_ID, SOUL_SAND_ID, SOUL_SOIL_ID, SUGAR_CANE_ID, TNT_ID, TOGGLE, VAULT_ID, VAULT_OPENED_ID, WATER_ID,
  anvilState, canPlaceAttached, chestFacing, collisionOf, doorId, faceIndex, facingFamilyOf, isFurnaceBlock,
  isFurnaceFamily, placeIdFor, shapeId,
} from '../blocks';
import { WORLD_HEIGHT } from '../constants';
import type { World } from '../world/chunk';
import type { DimensionId } from '../world/dimension';
import { boneMeal, plantResult, tillResult } from '../world/crops';
import { findPortalFrame, portalCells, portalIdFor } from '../world/portal';
import { breakSeconds, toolWear } from './mining';
import { enchantedDrops } from './enchant/playerEnchants';
import { enchLevel } from './enchant/stack';
import { E as ENCH } from './enchant/registry';
import { mineMult, type EffectSet } from './effects/effects';
import { EXHAUST_BREAK } from './playerStats';
import { insertEye } from './endPortal';
import { I, ITEM_BASE } from './itemIds';
import { itemDef } from './itemdb';
import { hoeConvert, shovelConvert } from './itemUse/toolUse';
import { glowBerryPlantCell, GLOW_BERRY_PLANT } from './itemUse/plant';
import { isEmpty, stackOf, type Stack } from './types';
import type { Inventory } from './inventory';
import { redstoneFloorOk, redstoneInteractable, redstonePlaceId, useRedstone } from '../world/redstone/actions';
import { RS } from '../world/redstone/defs';
import { splitChest, useUtilityBlock, utilityInteractable, utilityPlaceId } from './utility/rules';

const FLINT_AND_STEEL = ITEM_BASE + 8; // items.ts FLINT_AND_STEEL_ID
const nameOfId = (id: number): string => (id >= ITEM_BASE ? itemDef(id).name : BLOCKS[id]?.name ?? '');

/** Sounds / particles a block action causes. `d` is a block id unless noted. */
export type BlockFx =
  | 'break'     // block `d` broke at the cell
  | 'place'     // block `d` went in at the cell
  | 'door'      // a door / gate / trapdoor moved; d = 1 opened, 0 closed
  | 'ignite'    // flint and steel / fire charge struck; d = 1 when it primed TNT
  | 'bucket'    // d: 0 pour water, 1 fill water, 2 pour lava, 3 fill lava
  | 'fizz'      // water boiled away in the nether
  | 'bonemeal'  // green sparkles over the cell
  | 'note'      // a note block played; d = pitch (0..24) | instrument << 8
  | 'click'     // a lever / button / plate clicked; d = 1 on, 0 off
  | 'piston';   // a piston moved; d = 1 out, 0 in

/** A block face hit: the cell, the face normal (towards the player) and the block there. */
export interface BlockHit { x: number; y: number; z: number; nx: number; ny: number; nz: number; id: number }

/** The world a block action runs in, and what it can cause there. */
export interface BlockEnv {
  readonly world: World;
  readonly dim: DimensionId;
  canEdit(x: number, y: number, z: number): boolean;
  setBlock(x: number, y: number, z: number, id: number): void;
  rnd(): number;
  /** An item pops out of the cell (a block drop). */
  dropBlock(stack: Stack, x: number, y: number, z: number): void;
  dropXp(amount: number, x: number, y: number, z: number): void;
  fx(kind: BlockFx, x: number, y: number, z: number, d: number): void;
  primeTnt(x: number, y: number, z: number): void;
  /** A flame was placed at the cell: start it burning. */
  igniteFire(x: number, y: number, z: number): void;
  /** Would a block placed in this cell overlap a body (the actor, another player, a mob)? */
  occupied(x: number, y: number, z: number, id: number): boolean;
  /** A message for the acting player. */
  toast?(text: string): void;
  /** Called before a block goes: containers spill, piglins take offence. `drop` is false in creative. */
  broken?(x: number, y: number, z: number, id: number, drop: boolean): void;
  /** Run several edits as one (the client meshes once). */
  batch?(fn: () => void): void;
  /**
   * Client-side prediction in server mode: edits are provisional, nothing
   * random is rolled (bone meal only reports whether it would do something),
   * and the actor's inventory is left to the server.
   */
  readonly predict?: boolean;
}

/** The player doing it. */
export interface BlockActor {
  /** Feet position. */
  readonly x: number; readonly y: number; readonly z: number;
  readonly yaw: number;
  readonly creative: boolean;
  /** The held stack (may be empty) and the helmet slot. */
  readonly held: Stack;
  readonly helmet: Stack;
  readonly effects: EffectSet;
  consumeHeld(n: number): void;
  /** Wear the held tool by `n` uses (Unbreaking applies). */
  wearHeld(n: number): void;
  /** The held stack turns into another item (bucket <-> water bucket). */
  swapHeld(id: number): void;
  /** Hand over a stack; what does not fit is dropped at the player. */
  give(stack: Stack): void;
  exhaust(n: number): void;
  /** Eat from a block (cake): false when the player is not hungry. */
  feed?(hunger: number, saturation: number): boolean;
}

// ---- digging -----------------------------------------------------------------

/** Seconds of holding it takes this actor to break `blockId` (0 = instant, Infinity = never). */
export function digSeconds(blockId: number, a: Pick<BlockActor, 'held' | 'helmet' | 'effects' | 'creative'>, c: { onGround: boolean; inWater: boolean }): number {
  const secs = breakSeconds(blockId, isEmpty(a.held) ? 0 : a.held.id, {
    onGround: c.onGround, inWater: c.inWater, creative: a.creative,
    efficiency: enchLevel(a.held, ENCH.EFFICIENCY), aquaAffinity: enchLevel(a.helmet, ENCH.AQUA_AFFINITY) > 0,
  });
  return secs / Math.max(1e-6, mineMult(a.effects));
}

/** Can a player break this at all (not air, fluid or bedrock-hard)? */
export function diggable(id: number): boolean {
  return id !== AIR && IS_TARGETABLE[id] === 1 && Number.isFinite(breakSeconds(id, 0, { onGround: true, inWater: false, creative: false }));
}

/**
 * Break the block at a cell: drops, XP, tool wear and exhaustion in survival;
 * the other half of a bed goes with it; an underwater plant leaves its water.
 * Returns false when there was nothing to break.
 */
export function breakBlock(env: BlockEnv, a: BlockActor, x: number, y: number, z: number): boolean {
  const id = env.world.getBlock(x, y, z);
  if (id === AIR || IS_TARGETABLE[id] !== 1) return false;
  const survival = !a.creative;
  env.fx('break', x, y, z, id);
  if (!env.predict) {
    env.broken?.(x, y, z, id, survival);
    if (survival) {
      const held = a.held;
      const heldId = isEmpty(held) ? 0 : held.id;
      // a door's top half or a bed's head drops through its other half
      const dropId = DOOR_TOP_TO_BOTTOM[id] ?? (id === BED_HEAD_ID ? BED_FOOT_ID : id);
      const got = enchantedDrops(dropId, held, env.rnd);
      for (const s of got.drops) env.dropBlock(s, x, y, z);
      if (got.xp > 0) env.dropXp(got.xp, x + 0.5, y + 0.5, z + 0.5);
      const wear = toolWear(id, heldId);
      if (wear > 0 && !isEmpty(held)) a.wearHeld(wear);
      a.exhaust(EXHAUST_BREAK);
    }
  }
  splitChest(env, x, y, z, id); // the other half of a double chest stays, as a single one
  // the other half of a bed goes with it
  if (id === BED_FOOT_ID || id === BED_HEAD_ID) {
    const other = id === BED_FOOT_ID ? BED_HEAD_ID : BED_FOOT_ID;
    for (const [dx, dz] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
      if (env.world.getBlock(x + dx, y, z + dz) === other) {
        env.setBlock(x + dx, y, z + dz, AIR);
        break;
      }
    }
  }
  env.setBlock(x, y, z, BLOCKS[id]?.waterlogged ? WATER_ID : AIR);
  return true;
}

// ---- right-click on a block ----------------------------------------------------

/**
 * Would right-click act on this block (chest, crafting / enchanting / smithing
 * table, anvil, furnace, door, bed, vault)?
 */
export function interactableBlock(id: number): boolean {
  return chestFacing(id) >= 0 || id === BARREL_ID || id === CRAFTING_TABLE_ID || id === ENCHANTING_TABLE_ID
    || anvilState(id) !== null || id === SMITHING_TABLE_ID || id === GRINDSTONE_ID || isFurnaceBlock(id) || id === BREWING_STAND_ID
    || TOGGLE[id] !== undefined || redstoneInteractable(id) || utilityInteractable(id) || id === BED_FOOT_ID || id === BED_HEAD_ID || id === VAULT_ID || id === VAULT_OPENED_ID;
}

/** Doors, fence gates and trapdoors: blocks a right-click swings (a chest's lid is its window's business). */
export function togglable(id: number): boolean {
  return TOGGLE[id] !== undefined && chestFacing(id) < 0;
}

/**
 * A non-solid decoration in the way (a torch or plant sitting on a chest) must
 * not swallow a right-click meant for what it stands on: when the ray hits such
 * a block and the block below it is interactable, click that block instead.
 */
export function stepOverDecoration<H extends BlockHit>(world: Pick<World, 'getBlock'>, hit: H): H {
  if (collisionOf(hit.id).length !== 0) return hit;
  const below = world.getBlock(hit.x, hit.y - 1, hit.z);
  return interactableBlock(below) ? { ...hit, y: hit.y - 1, nx: 0, ny: 1, nz: 0, id: below } : hit;
}

/** Swing a door (both halves), fence gate or trapdoor. */
export function toggleBlock(env: BlockEnv, x: number, y: number, z: number): boolean {
  const id = env.world.getBlock(x, y, z);
  if (!togglable(id) || !env.canEdit(x, y, z)) return false;
  const toggled = TOGGLE[id]!;
  env.setBlock(x, y, z, toggled);
  env.fx('door', x, y, z, (DOOR_STATE[toggled]?.open ?? true) ? 1 : 0);
  // doors move both halves; the top id maps to the top id of the other state
  const oy = DOOR_BOTTOM_IDS.has(id) ? y + 1 : y - 1;
  const other = TOGGLE[env.world.getBlock(x, oy, z)];
  if (other !== undefined && DOOR_STATE[id]) env.setBlock(x, oy, z, other);
  return true;
}

/** Horizontal facing of a yaw: the face (0 +Z, 1 +X, 2 -Z, 3 -X) the gaze points along. */
export function facingFromYaw(yaw: number): 0 | 1 | 2 | 3 {
  // forward vector: (-sin(yaw), -cos(yaw)) in (X, Z); ties break toward +X / -Z
  const fx = -Math.sin(yaw);
  const fz = -Math.cos(yaw);
  if (Math.abs(fx) > Math.abs(fz)) return fx > 0 ? 1 : 3;
  return fz > 0 ? 0 : 2;
}

/**
 * Hinge side for a door placed at (x, y, z) facing `facing`, as vanilla's
 * DoorBlock.getHinge: the side with more full blocks beside the doorway gets
 * the hinge, a neighbouring door makes this one its mirror (double doors), and
 * otherwise the half of the block face the player clicked (dx, dz within the
 * cell) decides. 0 = left, 1 = right, as the placer sees it.
 */
export function doorHinge(world: Pick<World, 'getBlock'>, x: number, y: number, z: number, facing: number, dx: number, dz: number): 0 | 1 {
  const [fx, fz] = [[0, 1], [1, 0], [0, -1], [-1, 0]][facing];
  const lx = fz, lz = -fx; // placer's left; right is (-lx, -lz)
  const get = (ox: number, oy: number, oz: number): number => world.getBlock(x + ox, y + oy, z + oz);
  const full = (id: number): number => (collisionOf(id) === FULL_CELL_BOXES ? 1 : 0);
  const score = -full(get(lx, 0, lz)) - full(get(lx, 1, lz)) + full(get(-lx, 0, -lz)) + full(get(-lx, 1, -lz));
  const leftDoor = DOOR_BOTTOM_IDS.has(get(lx, 0, lz));
  const rightDoor = DOOR_BOTTOM_IDS.has(get(-lx, 0, -lz));
  if ((leftDoor && !rightDoor) || score > 0) return 1;
  if ((rightDoor && !leftDoor) || score < 0) return 0;
  // vanilla: (stepX >= 0 || dz >= .5) && (stepX <= 0 || dz <= .5) && (stepZ >= 0 || dx <= .5) && (stepZ <= 0 || dx >= .5) ? LEFT : RIGHT
  const left = (fx >= 0 || dz >= 0.5) && (fx <= 0 || dz <= 0.5) && (fz >= 0 || dx <= 0.5) && (fz <= 0 || dx >= 0.5);
  return left ? 0 : 1;
}

/** May a placed block take this cell (air, a fluid, fire)? */
const replaceable = (id: number): boolean => id === AIR || IS_TARGETABLE[id] === 0;

/**
 * Place block `sel` against the face `hit`. `click` is the point of the face
 * that was clicked, in world space (slab / stair halves and door hinges come
 * from it). Returns true when a block went in; the caller uses up the item.
 */
export function placeBlock(env: BlockEnv, a: BlockActor, sel: number, hit: BlockHit, click: readonly [number, number, number]): boolean {
  if (sel <= AIR || sel >= ITEM_BASE || !BLOCKS[sel]) return false;
  const world = env.world;
  const tx = hit.x + hit.nx;
  const ty = hit.y + hit.ny;
  const tz = hit.z + hit.nz;
  const facing = facingFromYaw(a.yaw);
  const clickY = click[1] - hit.y; // height of the click within the hit block (1 / 0 on its top / bottom face)
  // a slab on the matching half of a slab of the same kind makes a full block
  const st = SHAPE_STATE[sel];
  if (st && st.kind === 'slab') {
    const on = SHAPE_STATE[hit.id];
    const joins = on && on.kind === 'slab' && on.fam === st.fam && on.half !== 2 && (hit.ny === 0
      ? (on.half === 0) === (clickY > 0.5) : (on.half === 0) === (hit.ny === 1));
    if (joins) {
      if (!env.canEdit(hit.x, hit.y, hit.z)) return false;
      const full = SHAPE_FAMILIES[st.fam].double;
      env.setBlock(hit.x, hit.y, hit.z, full);
      env.fx('place', hit.x, hit.y, hit.z, full);
      return true;
    }
  }
  if (ty < 0 || ty >= WORLD_HEIGHT || !env.canEdit(tx, ty, tz) || !replaceable(world.getBlock(tx, ty, tz))) return false;
  // Support check: torches / plants / cacti need something solid next to the
  // placement cell, and a torch only gets its mount from the face it was
  // clicked on (placeIdFor / canPlaceAttached in blocks.ts).
  // redstone components take their orientation from the clicked face or from where the player looks
  const rsPlace = redstonePlaceId(sel, hit.nx, hit.ny, hit.nz, click[0] - a.x, click[1] - (a.y + 1.62), click[2] - a.z);
  if (rsPlace === -1 || !redstoneFloorOk(world, sel, tx, ty, tz)) return false;
  const selId = rsPlace ?? placeIdFor(sel, hit.nx, hit.ny, hit.nz);
  const supported = canPlaceAttached(selId, (dx, dy, dz) => IS_SOLID[world.getBlock(tx + dx, ty + dy, tz + dz)] === 1);
  // nether wart only takes root in soul sand (vanilla), so a stray one cannot be planted on grass
  const wartBad = selId === NETHER_WART_IDS[0] && world.getBlock(tx, ty - 1, tz) !== SOUL_SAND_ID;
  if (!supported || wartBad || env.occupied(tx, ty, tz, selId)) return false;
  // Directional cubes (furnaces, chests): the inventory only carries the
  // +Z-facing variant, so the rotation comes from the player's yaw. A furnace
  // turns its door to face the player who placed it; the chests keep their own
  // convention and point the latch along the gaze.
  let placeId = selId;
  // containers and utility blocks (game/utility/rules.ts): double chests, hoppers, dispensers, signs, chains
  const util = utilityPlaceId(env, a, selId, hit, tx, ty, tz, facing, [click[0] - a.x, click[1] - (a.y + 1.62), click[2] - a.z]);
  if (util === -1) return false;
  const family = util === undefined ? facingFamilyOf(sel) : undefined;
  if (util !== undefined) placeId = util;
  else if (family) {
    placeId = family[isFurnaceFamily(family) ? (facing + 2) % 4 : facing];
  } else if (sel === BED_FOOT_ID) {
    // two cells: the foot where the player clicked, the head along the gaze
    const [dx, dz] = [[0, 1], [1, 0], [0, -1], [-1, 0]][facing];
    const hx = tx + dx, hz = tz + dz;
    if (world.getBlock(hx, ty, hz) !== AIR || !env.canEdit(hx, ty, hz)) { env.toast?.('Bed needs an empty cell in front of you'); return false; }
    if (IS_SOLID[world.getBlock(hx, ty - 1, hz)] !== 1) { env.toast?.('Bed needs a solid floor'); return false; }
    const under = world.getBlock(tx, ty - 1, tz);
    if (under !== AIR && IS_SOLID[under] !== 1) { env.toast?.('Bed needs a solid floor'); return false; }
    if (env.occupied(hx, ty, hz, BED_HEAD_ID)) return false;
    env.setBlock(tx, ty, tz, BED_FOOT_ID);
    env.setBlock(hx, ty, hz, BED_HEAD_ID);
    env.fx('place', tx, ty, tz, BED_FOOT_ID);
    return true;
  } else if (st) {
    // slabs, stairs, gates and trapdoors: the state comes from where the player looks and clicked
    const fam = SHAPE_FAMILIES[st.fam];
    const upper = hit.ny === -1 ? true : hit.ny === 1 ? false : clickY > 0.5;
    if (st.kind === 'slab') placeId = shapeId(fam, { half: upper ? 1 : 0 });
    else if (st.kind === 'stair') placeId = shapeId(fam, { facing, half: upper ? 1 : 0 });
    else if (st.kind === 'gate') placeId = shapeId(fam, { facing });
    else if (st.kind === 'trapdoor') placeId = shapeId(fam, { facing: (facing + 2) & 3, half: upper ? 1 : 0 });
  } else if (DOOR_STATE[sel]) {
    // two cells tall: both have to be free, and the pair goes in bottom-first
    // so the top half finds its support in place
    if (ty + 1 >= WORLD_HEIGHT || world.getBlock(tx, ty + 1, tz) !== AIR) { env.toast?.('Door needs two free cells'); return false; }
    if (env.occupied(tx, ty + 1, tz, sel)) return false;
    const hinge = doorHinge(world, tx, ty, tz, facing, click[0] - tx, click[2] - tz);
    const kind = DOOR_STATE[sel]!.kind;
    env.setBlock(tx, ty, tz, doorId(kind, facing, hinge, false, false));
    env.setBlock(tx, ty + 1, tz, doorId(kind, facing, hinge, false, true));
    env.fx('place', tx, ty, tz, sel);
    return true;
  }
  env.setBlock(tx, ty, tz, placeId);
  env.fx('place', tx, ty, tz, placeId);
  return true;
}

/** Is this a bucket the block rules handle (empty, water, lava)? */
export const isBucket = (id: number): boolean => id === I.BUCKET || id === I.WATER_BUCKET || id === I.LAVA_BUCKET;

/**
 * Right click with a bucket. An empty bucket's `hit` is the fluid source it
 * points at (its ray stops at water and lava sources as well as blocks); a
 * full one's is the block face it pours against. Returns true when it did
 * something.
 */
export function useBucket(env: BlockEnv, a: BlockActor, item: number, hit: BlockHit): boolean {
  const world = env.world;
  const swap = (id: number): void => { if (!a.creative) a.swapHeld(id); };
  if (item === I.BUCKET) {
    // only a source block is scooped; flowing water / lava is left alone
    if ((hit.id !== WATER_ID && hit.id !== LAVA_ID) || !env.canEdit(hit.x, hit.y, hit.z)) return false;
    env.setBlock(hit.x, hit.y, hit.z, AIR);
    env.fx('bucket', hit.x, hit.y, hit.z, hit.id === WATER_ID ? 1 : 3);
    swap(hit.id === WATER_ID ? I.WATER_BUCKET : I.LAVA_BUCKET);
    return true;
  }
  if (item !== I.WATER_BUCKET && item !== I.LAVA_BUCKET) return false;
  // plants and ground cover are flooded in place; otherwise fill the cell in front of the face
  const inPlace = IS_SOLID[hit.id] === 0 && hit.id !== SUGAR_CANE_ID;
  const x = inPlace ? hit.x : hit.x + hit.nx;
  const y = inPlace ? hit.y : hit.y + hit.ny;
  const z = inPlace ? hit.z : hit.z + hit.nz;
  const there = world.getBlock(x, y, z);
  if (IS_SOLID[there] !== 0 || there === SUGAR_CANE_ID || !env.canEdit(x, y, z)) return false;
  if (item === I.LAVA_BUCKET) {
    if (IS_LAVA[there] !== 0) return false;
    env.setBlock(x, y, z, LAVA_ID);
    env.fx('bucket', x, y, z, 2);
    swap(I.BUCKET);
    return true;
  }
  if (BLOCKS[there]?.waterlogged) return false;
  if (env.dim === 'nether') {
    // the nether is ultrawarm: water boils away with a puff of smoke, the bucket empties
    env.fx('fizz', x, y, z, 0);
    swap(I.BUCKET);
    return true;
  }
  env.setBlock(x, y, z, WATER_ID);
  env.fx('bucket', x, y, z, 0);
  swap(I.BUCKET);
  return true;
}

/**
 * Flint and steel or a fire charge on a block face: prime TNT, light a nether
 * portal inside an obsidian frame, or set a flame on top of the block / in the
 * cell beside the clicked side. Returns true when the click was consumed.
 */
export function ignite(env: BlockEnv, a: BlockActor, hit: BlockHit, charge: boolean): boolean {
  const world = env.world;
  // a fire charge is used up, flint and steel only wears
  const spend = (): void => { if (a.creative) return; if (charge) a.consumeHeld(1); else a.wearHeld(1); };
  // TNT takes the spark instead of a flame block (a 4 s fuse, world/tnt.ts)
  if (hit.id === TNT_ID) {
    if (!env.canEdit(hit.x, hit.y, hit.z)) return false;
    if (!env.predict) env.primeTnt(hit.x, hit.y, hit.z);
    env.fx('ignite', hit.x, hit.y, hit.z, 1);
    spend();
    return true;
  }
  let fx: number, fy: number, fz: number;
  if (hit.ny === 1) {
    fx = hit.x; fy = hit.y + 1; fz = hit.z; // top of a block: fire on top
  } else {
    // side face: fire in the cell beside it, but only if that cell has a floor
    fx = hit.x + hit.nx; fy = hit.y; fz = hit.z + hit.nz;
    if (IS_SOLID[world.getBlock(fx, fy - 1, fz)] !== 1) return false;
  }
  if (world.getBlock(fx, fy, fz) !== AIR || !env.canEdit(fx, fy, fz)) return false;
  // inside an obsidian frame: light a nether portal instead of a flame
  const frame = findPortalFrame(world, fx, fy, fz);
  if (frame) {
    const fill = (): void => { for (const [px, py, pz] of portalCells(frame)) env.setBlock(px, py, pz, portalIdFor(frame.axis)); };
    if (env.batch) env.batch(fill); else fill();
    env.fx('ignite', fx, fy, fz, 0);
    spend();
    return true;
  }
  if (fy >= 192 || fy < 0) return false;
  env.fx('ignite', fx, fy, fz, 0);
  // soul sand / soul soil burn with blue soul fire, which never goes out
  const below = world.getBlock(fx, fy - 1, fz);
  if (below === SOUL_SAND_ID || below === SOUL_SOIL_ID) {
    env.setBlock(fx, fy, fz, SOUL_FIRE_ID);
    spend();
    return true;
  }
  env.setBlock(fx, fy, fz, FIRE_ID);
  // the new flame starts ticking straight away: it spreads into nearby fuel or burns itself out
  if (!env.predict) env.igniteFire(fx, fy, fz);
  spend();
  return true;
}

/** An empty bottle on water: survival swaps one bottle for a water bottle. */
export function fillBottle(env: BlockEnv, a: BlockActor, hit: BlockHit): boolean {
  if (IS_WATERY[hit.id] !== 1) return false;
  if (!a.creative && !env.predict) {
    a.consumeHeld(1);
    a.give(stackOf(I.POTION));
  }
  return true;
}

/**
 * Bone meal, hoe tilling and dirt conversion, shovel paths, glow berries on a
 * ceiling, seeds / carrots / potatoes on farmland. Returns true when the item
 * did something to the block.
 */
export function useFarmItem(env: BlockEnv, a: BlockActor, hit: BlockHit): boolean {
  const held = a.held;
  if (isEmpty(held)) return false;
  const world = env.world;
  const survival = !a.creative;
  if (held.id === I.BONE_MEAL) {
    if (env.predict) {
      // the server rolls the growth; here only "would it be used?"
      return boneMeal({ world, canEdit: (x, y, z) => env.canEdit(x, y, z), setBlock: () => { /* dry run */ } }, hit.x, hit.y, hit.z, Math.random).length > 0;
    }
    const cells = boneMeal({ world, canEdit: (x, y, z) => env.canEdit(x, y, z), setBlock: (x, y, z, id) => { env.setBlock(x, y, z, id); } },
      hit.x, hit.y, hit.z, env.rnd);
    if (cells.length === 0) return false;
    for (const [cx, cy, cz] of cells.slice(0, 8)) env.fx('bonemeal', cx, cy, cz, 0);
    if (survival) a.consumeHeld(1);
    return true;
  }
  const toolType = itemDef(held.id).tool?.type;
  if ((toolType === 'hoe' || toolType === 'shovel') && env.canEdit(hit.x, hit.y, hit.z)) {
    const conv = toolType === 'hoe' ? hoeConvert(world, hit.x, hit.y, hit.z, hit.ny) : shovelConvert(world, hit.x, hit.y, hit.z, hit.ny);
    if (conv) {
      env.setBlock(hit.x, hit.y, hit.z, conv);
      env.fx('place', hit.x, hit.y, hit.z, conv);
      if (survival) a.wearHeld(1);
      return true;
    }
  }
  if (toolType === 'hoe' && hit.ny >= 0) {
    const to = tillResult(world, hit.x, hit.y, hit.z, hit.ny);
    if (!to || !env.canEdit(hit.x, hit.y, hit.z)) return false;
    env.setBlock(hit.x, hit.y, hit.z, to);
    env.fx('place', hit.x, hit.y, hit.z, to);
    if (survival) a.wearHeld(1);
    return true;
  }
  // glow berries on a ceiling hang cave vines (game/itemUse/plant.ts)
  const vine = glowBerryPlantCell(world, held.id, hit.x, hit.y, hit.z, hit.ny);
  if (vine && env.canEdit(vine[0], vine[1], vine[2])) {
    env.setBlock(vine[0], vine[1], vine[2], GLOW_BERRY_PLANT);
    env.fx('place', vine[0], vine[1], vine[2], GLOW_BERRY_PLANT);
    if (survival) a.consumeHeld(1);
    return true;
  }
  const crop = plantResult(world, held.id, hit.x, hit.y, hit.z, hit.ny);
  if (!crop || !env.canEdit(hit.x, hit.y + 1, hit.z)) return false;
  env.setBlock(hit.x, hit.y + 1, hit.z, crop);
  env.fx('place', hit.x, hit.y + 1, hit.z, crop);
  if (survival) a.consumeHeld(1);
  return true;
}

/** An eye of ender held against an empty end portal frame goes in; the twelfth opens the portal. */
export function insertEyeOfEnder(env: BlockEnv, a: BlockActor, x: number, y: number, z: number): boolean {
  if (env.world.getBlock(x, y, z) !== END_PORTAL_FRAME_ID || isEmpty(a.held) || a.held.id !== I.EYE_OF_ENDER || !env.canEdit(x, y, z)) return false;
  const r = insertEye({ getBlock: (bx, by, bz) => env.world.getBlock(bx, by, bz), setBlock: (bx, by, bz, v) => { env.setBlock(bx, by, bz, v); } }, x, y, z);
  if (r === 'none') return false;
  if (!a.creative) a.consumeHeld(1);
  if (r === 'activated') env.toast?.('The portal to the End opens');
  return true;
}

/** Where the caller plugs in what this module does not own. Each returns true when it consumed the click. */
export interface UseOnHooks {
  sneaking: boolean;
  /** A block with its own behaviour (containers, tables, beds): open it, sleep in it... */
  blockUse?(x: number, y: number, z: number, id: number): boolean;
  /** An item with its own behaviour on a block (spawn eggs, boats, end crystals...). */
  itemUse?(hit: BlockHit): boolean;
  /** A block was placed by hand (golem / wither patterns). */
  placed?(x: number, y: number, z: number, id: number): void;
}

/**
 * One right-click on a block face, in vanilla's order: the block's own
 * interaction first (skipped while sneaking), then the held item, then
 * placing the held block. Returns true when the click did something.
 *
 * For an empty bucket or a glass bottle `hit` is the fluid cell the ray
 * stopped at; for everything else it is a targetable block.
 */
export function useOnBlock(env: BlockEnv, a: BlockActor, hit: BlockHit, click: readonly [number, number, number], hooks: UseOnHooks): boolean {
  const held = a.held;
  const heldId = isEmpty(held) ? 0 : held.id;
  if (IS_TARGETABLE[hit.id] === 1 && !hooks.sneaking) {
    const ih = stepOverDecoration(env.world, hit);
    if (insertEyeOfEnder(env, a, ih.x, ih.y, ih.z)) return true;
    if (hooks.blockUse?.(ih.x, ih.y, ih.z, ih.id)) return true;
    if (useRedstone(env, ih.x, ih.y, ih.z, ih.id)) return true;
    if (useUtilityBlock(env, a, ih, nameOfId)) return true;
    if (toggleBlock(env, ih.x, ih.y, ih.z)) return true;
  }
  if (heldId === 0) return false;
  if (hooks.itemUse?.(hit)) return true;
  if (heldId === I.BUCKET || heldId === I.GLASS_BOTTLE) {
    return heldId === I.BUCKET ? useBucket(env, a, heldId, hit) : fillBottle(env, a, hit);
  }
  if (IS_TARGETABLE[hit.id] !== 1) return false;
  if (heldId >= ITEM_BASE) {
    if (heldId === I.FIRE_CHARGE) return ignite(env, a, hit, true);
    if (heldId === FLINT_AND_STEEL) return ignite(env, a, hit, false);
    if (heldId === I.WATER_BUCKET || heldId === I.LAVA_BUCKET) return useBucket(env, a, heldId, hit);
    if (heldId === I.REDSTONE || heldId === I.STRING) {
      // redstone dust and tripwire are placed with their items
      if (!placeBlock(env, a, heldId === I.REDSTONE ? RS.DUST : RS.TRIPWIRE, hit, click)) return false;
      if (!a.creative) a.consumeHeld(1);
      return true;
    }
    return useFarmItem(env, a, hit);
  }
  if (!placeBlock(env, a, heldId, hit, click)) return false;
  const px = hit.x + hit.nx, py = hit.y + hit.ny, pz = hit.z + hit.nz;
  if (!env.predict) hooks.placed?.(px, py, pz, env.world.getBlock(px, py, pz));
  if (!a.creative) a.consumeHeld(1);
  return true;
}

/** Face normal for a wire face index (blocks.ts FACE_DIRS order: +X -X +Y -Y +Z -Z), or null. */
export function faceNormal(face: number): readonly [number, number, number] | null {
  switch (face) {
    case 0: return [1, 0, 0];
    case 1: return [-1, 0, 0];
    case 2: return [0, 1, 0];
    case 3: return [0, -1, 0];
    case 4: return [0, 0, 1];
    case 5: return [0, 0, -1];
    default: return null;
  }
}
export const faceOf = (nx: number, ny: number, nz: number): number => faceIndex(nx, ny, nz);

/**
 * Turn one item of a stack into another item (an empty bucket filled, a full
 * one emptied): a single item changes in place, one of several is taken off
 * the stack and the new item handed over through `give`.
 */
export function swapHeld(inv: Inventory, id: number, give: (s: Stack) => void): void {
  const held = inv.held;
  if (isEmpty(held)) return;
  if (held.count <= 1) { held.id = id; held.dmg = 0; inv.changed(); return; }
  inv.consumeHeld(1);
  give(stackOf(id));
}
